// components/Header.js
import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import {
  Header,
  HeaderName,
  HeaderGlobalBar,
  HeaderGlobalAction,
} from "@carbon/react";
import { useAuth } from "../auth/AuthContext";
import { Logout, Switcher, Home, ArrowLeft } from "@carbon/icons-react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { buildMenuItems } from "../nav/menuConfig";
import MenuList from "./MenuList";
import "./Header.css";
import CustomModal from "./CustomModal";
import CustomButton from "./CustomButton";

/** ダッシュボード配下かどうかの判定 */
const DASHBOARD_PATHS = new Set([
  "/summary",
  "/basic-info",
  "/consultation-records",
  "/consultation",
  "/ai-interview-scores",
  "/ai-interview-questions",
  "/test-results",
]);

export default function AppHeader({ hasPatientContext = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, role, isAuthed, signOut } = useAuth(); // ← ここが重要
  // const { role, isAuthed, signOut } = useAuth();
  const cancelBtnRef = useRef(null);

  // sessionStorage の値はコンポーネント内で読む
  const [patient, setPatient] = useState(null);
  const [attendingDoctor, setAttendingDoctor] = useState(null);

  useEffect(() => {
    const rawPatient = sessionStorage.getItem("patient");
    const rawAttending = sessionStorage.getItem("attending_doctor");
    setPatient(rawPatient ? JSON.parse(rawPatient) : null);
    setAttendingDoctor(rawAttending ? JSON.parse(rawAttending) : null);
  }, [auth]); // ログイン/ログアウト時に同期される

  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  const items = useMemo(() => buildMenuItems(role), [role]);

  const isPatient = role === "patient";
  const isAttending = role === "attending_physician";
  const isExpert = role === "expert_physician";

  // 現在ページがダッシュボード群か？
  const isDashboardRoute = DASHBOARD_PATHS.has(location.pathname);

  // 患者は文脈UIを出さない
  const showPatientContext = !isPatient && hasPatientContext;

  // 左アクション表示条件（PCのみ・患者文脈あり）
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      setIsDesktop(!mobile);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const hasLeftActions = isDesktop && showPatientContext;

  // 新ルート
  const patientListPath = isAttending
    ? "/patient-selection"
    : isExpert
    ? "/ap-selection"
    : null;
    
  const DUMMY = useMemo(() => {
    const rawAuth = sessionStorage.getItem("auth");
    const ssAuth = rawAuth ? JSON.parse(rawAuth) : null;
    const selfId = auth?.id ?? ssAuth?.id ?? null;

    return {
      patient:
        isPatient
          ? { id: selfId }
          : patient && patient.research_id
          ? { id: patient.research_id }
          : null,

      attending_physician:
        isAttending
          ? { id: selfId }
          : attendingDoctor &&
            (attendingDoctor.attending_doctor_id || attendingDoctor.id)
          ? {
              id:
                attendingDoctor.attending_doctor_id ??
                attendingDoctor.id,
            }
          : null,

      expert_physician:
        isExpert
          ? { id: selfId }
          : null,
    };
  }, [isPatient, isAttending, isExpert, auth, patient, attendingDoctor]);

  // タイトル文字列を組み立て
  const { parts, fullText } = useMemo(() => {
    if (!role) return { parts: ["MS診察支援AIツール"], fullText: "MS診察支援AIツール" };

    const dummyUser =
      (isPatient && DUMMY.patient) ||
      (isAttending && DUMMY.attending_physician) ||
      (isExpert && DUMMY.expert_physician) ||
      { id: "—" };

    /* ▼患者ログイン時 */
    if (isPatient) {
      const pid = dummyUser.id ?? "—";
      return { parts: [`ログイン中：${pid}`], fullText: `ログイン中：${pid}` };
    }

    /* ▼医師ログインで患者文脈を出さない場合（例: patient context hidden） */
    if (!showPatientContext) {
      const p = [`ログイン中：${dummyUser.id ?? "—"}`];
      return { parts: p, fullText: p.join("｜") };
    }

    // 医師 + 患者文脈あり
    const pat = DUMMY.patient;
    const att = DUMMY.attending_physician;
    const exp = DUMMY.expert_physician;
    const p = [];
    if (pat) p.push(`患者：${pat.id ?? "—"}`);
    if (att && (isAttending || isExpert)) {
      p.push(`主治医：${att.id ?? "—"}`);
    }
    if (exp && isExpert) {
      p.push(`エキスパート医師：${exp.id ?? "—"}`);
    }
    return { parts: p, fullText: p.join("｜") };
  }, [role, isPatient, isAttending, isExpert, showPatientContext, DUMMY]);

  const headerWrapRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(48);
  useLayoutEffect(() => {
    const update = () => {
      const el = headerWrapRef.current?.querySelector(".cds--header");
      const h = el ? el.getBoundingClientRect().height : 48;
      setHeaderHeight(h);
      document.documentElement.style.setProperty("--app-header-h", `${h}px`);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [isMobile, fullText]);

  const handleLogout = () => {
    signOut();
    setLogoutModalOpen(false);
    navigate("/login", { replace: true });
  };

  // 中央タイトル（PC & 左アクションありの時だけ出す）
  const CenterTitle = hasLeftActions ? (
    <div
      className="app-header-centerTitle"
      onClick={() => showPatientContext && navigate("/summary")}
      role={showPatientContext ? "button" : undefined}
      title={fullText}
    >
      <span className="title-chunks" aria-label={fullText}>
        {parts.map((t, i) => (
          <span key={i} className="title-chunk">
            {t}
          </span>
        ))}
      </span>
    </div>
  ) : null;

  return (
    <div ref={headerWrapRef} className="cds--theme-g100 app-header-autosize">
      <Header aria-label="アプリヘッダー" className="app-header-inner">
        {/* 左：患者一覧/ダッシュボードリンク＋タイトル（PCでは必要に応じて不可視化） */}
        <div
          className="app-header-left"
          data-left-actions={hasLeftActions ? "1" : "0"}
          style={{ display: "flex", alignItems: "center", gap: hasLeftActions ? "0.75rem" : "0.25rem" }}
        >
          {hasLeftActions && patientListPath && (
            <Link to={patientListPath} className="app-header-link" style={{ padding: "0.375rem 0.5rem" }}>
              <ArrowLeft />
              <span>患者一覧</span>
            </Link>
          )}

          {hasLeftActions && (
            <Link
              to="/summary"
              className="app-header-link app-header-link--dashboard"
              aria-label="ダッシュボードへ"
              title="ダッシュボード"
              style={{ padding: "0.375rem 0.5rem" }}
            >
              <Home />
              <span>ダッシュボード</span>
            </Link>
          )}

          <HeaderName
            prefix=""
            onClick={() => showPatientContext && navigate("/summary")}
            className="app-header-titleLeft"
            style={{ cursor: showPatientContext ? "pointer" : "default" }}
            title={fullText}
          >
            <span className="title-chunks" aria-label={fullText}>
              {parts.map((t, i) => (
                <span key={i} className="title-chunk">
                  {t}
                </span>
              ))}
            </span>
          </HeaderName>
        </div>

        {/* 右：モバイル=ハンバーガー / PC=ログアウト */}
        <HeaderGlobalBar>
          {isMobile && isAuthed && (
            <HeaderGlobalAction aria-label="メニューを開く" onClick={() => setSidebarOpen((v) => !v)}>
              <Switcher />
            </HeaderGlobalAction>
          )}
          {isDesktop && isAuthed && (
            <button
              type="button"
              onClick={() => setLogoutModalOpen(true)}
              className="app-header-link app-header-logout"
            >
              <Logout />
              <span>ログアウト</span>
            </button>
          )}
        </HeaderGlobalBar>

        {/* PC専用：ど真ん中のタイトル（左アクションがある時のみ） */}
        {CenterTitle}
      </Header>

      {/* モバイルメニュー（右パネル） */}
      {isMobile && isSidebarOpen && (
        <div
          style={{
            position: "fixed",
            top: `var(--app-header-h, ${headerHeight}px)`,
            right: 0,
            width: 300, // ← ここはリクエスト通り 300px
            backgroundColor: "#ffffff",
            zIndex: 1000,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            padding: "1rem",
            height: `calc(100vh - var(--app-header-h, ${headerHeight}px))`,
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
          }}
        >
          {/* ▼ ダッシュボード群の時だけ 近道リンク＋MenuList を表示 */}
          {showPatientContext && isDashboardRoute && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "0.5rem" }}>
              {patientListPath && (
                <button
                  onClick={() => {
                    navigate(patientListPath);
                    setSidebarOpen(false);
                  }}
                  className="app-header-menu-btn"
                >
                  <ArrowLeft />
                  <span>患者一覧</span>
                </button>
              )}
              <button
                onClick={() => {
                  navigate("/summary");
                  setSidebarOpen(false);
                }}
                className="app-header-menu-btn app-header-menu-btn--dashboard"
              >
                <Home />
                <span>ダッシュボード</span>
              </button>
            </div>
          )}

          {/* ▼ ダッシュボード群のみ：メニュー一覧を表示 */}
          {isDashboardRoute && <MenuList items={items} onNavigate={() => setSidebarOpen(false)} />}

          {/* ▼ 共通：一番下にログアウトだけは表示 */}
          {isAuthed && (
            <button
              onClick={() => {
                setSidebarOpen(false);
                setLogoutModalOpen(true);
              }}
              className="app-header-menu-btn"
              style={{ marginTop: "auto" }}
            >
              <Logout />
              <span>ログアウト</span>
            </button>
          )}
        </div>
      )}

      {/* ログアウトモーダル */}
      <CustomModal
        open={isLogoutModalOpen}
        title="ログアウト確認"
        onClose={() => setLogoutModalOpen(false)}
        size="sm"
        closeOnOverlay={true}
        preventCloseOnEsc={false}
        initialFocusRef={cancelBtnRef}
        containerClassName="custom-modal-width"
        footer={
          <>
            <CustomButton
              kind="secondary"
              onClick={() => setLogoutModalOpen(false)}
              ref={cancelBtnRef}
              data-autofocus
            >
              キャンセル
            </CustomButton>
            <CustomButton kind="danger" onClick={handleLogout}>
              ログアウト
            </CustomButton>
          </>
        }
      >
        <p style={{ marginTop: 0 }}>ログアウトしますか？</p>
      </CustomModal>
    </div>
  );
}
